/**
 * Portal project inspiration board service
 *
 * One Firestore document per portal project (deal) at `portalProjectBoards/{dealId}`.
 * Board images live in Firebase Storage under `portalProjectBoards/{dealId}/...`.
 * Legacy lead-scoped boards lived at `leadBoards/{leadId}` and are copied on first
 * access when `migrateFromLeadId` is supplied.
 */

import { db, storage } from './firebase';
import {
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import type { LeadBoard, BoardNode, BoardViewport, BoardDrawing } from '../types';
import { compressImageToWebP, isSupportedImageType } from './imageCompression';
import { sanitizeFileNameForStoragePath } from './storageFileNames';

const COLLECTION = 'portalProjectBoards';
const LEGACY_COLLECTION = 'leadBoards';
const STORAGE_PREFIX = 'portalProjectBoards';

/** Cloud Function that fetches OpenGraph metadata for a URL (avoids browser CORS). */
const LINK_PREVIEW_ENDPOINT =
  'https://us-central1-lctf-projects.cloudfunctions.net/getLinkPreview';

export interface UploadedBoardImage {
  url: string;
  storagePath: string;
  naturalWidth?: number;
  naturalHeight?: number;
}

export interface LinkPreviewResult {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  domain?: string;
}

export interface SubscribeBoardOptions {
  /** Copy nodes from `leadBoards/{leadId}` when this project board is empty. */
  migrateFromLeadId?: string;
}

/** Remove `undefined` values so Firestore `setDoc` does not reject the payload. */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined) out[k] = stripUndefined(v as unknown);
    });
    return out as T;
  }
  return value;
}

async function migrateLegacyLeadBoardIfNeeded(
  dealId: string,
  leadId: string,
): Promise<void> {
  const projectRef = doc(db, COLLECTION, dealId);
  const projectSnap = await getDoc(projectRef);
  const existingNodes = projectSnap.exists()
    ? ((projectSnap.data().nodes as BoardNode[]) || [])
    : [];
  if (existingNodes.length > 0) return;

  const legacySnap = await getDoc(doc(db, LEGACY_COLLECTION, leadId));
  if (!legacySnap.exists()) return;
  const legacy = legacySnap.data();
  const legacyNodes = (legacy.nodes as BoardNode[]) || [];
  if (legacyNodes.length === 0) return;

  await setDoc(
    projectRef,
    stripUndefined({
      dealId,
      nodes: legacyNodes,
      viewport: legacy.viewport as BoardViewport | undefined,
      updatedAt: Timestamp.now(),
      updatedBy: 'migration-from-lead-board',
      migratedFromLeadId: leadId,
    }),
    { merge: true },
  );
}

/**
 * Subscribe to a portal project's board in real time. Returns an unsubscribe function.
 */
export function subscribeToBoard(
  dealId: string,
  callback: (board: LeadBoard | null) => void,
  onError?: (err: Error) => void,
  options?: SubscribeBoardOptions,
): () => void {
  let unsub: (() => void) | null = null;
  let cancelled = false;

  void (async () => {
    if (options?.migrateFromLeadId) {
      try {
        await migrateLegacyLeadBoardIfNeeded(dealId, options.migrateFromLeadId);
      } catch (err) {
        console.warn('[leadBoardService] legacy board migration failed:', err);
      }
    }
    if (cancelled) return;

    const docRef = doc(db, COLLECTION, dealId);
    unsub = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        const data = snap.data() as Record<string, unknown>;
        const updatedAtRaw = data.updatedAt as { toDate?: () => Date } | undefined;
        callback({
          dealId,
          nodes: (data.nodes as BoardNode[]) || [],
          drawings: (data.drawings as BoardDrawing[]) || [],
          viewport: data.viewport as BoardViewport | undefined,
          updatedAt: updatedAtRaw?.toDate ? updatedAtRaw.toDate() : new Date(),
          updatedBy: data.updatedBy as string | undefined,
        });
      },
      (err) => {
        console.error('[leadBoardService] subscribe error:', err);
        onError?.(err);
      },
    );
  })();

  return () => {
    cancelled = true;
    unsub?.();
  };
}

/**
 * Persist the board (merge write so concurrent metadata is not clobbered).
 * Last-write-wins per the chosen single-user model.
 */
export async function saveBoard(
  dealId: string,
  nodes: BoardNode[],
  viewport: BoardViewport | undefined,
  updatedBy: string | undefined,
  drawings?: BoardDrawing[],
): Promise<void> {
  const docRef = doc(db, COLLECTION, dealId);
  const payload = stripUndefined({
    dealId,
    nodes,
    drawings: drawings || [],
    viewport,
    updatedAt: Timestamp.now(),
    updatedBy: updatedBy || 'unknown',
  });
  await setDoc(docRef, payload, { merge: true });
}

/** Read an image blob's natural pixel dimensions. */
function readImageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

/**
 * Compress (WebP) and upload an image file to a project board storage area.
 */
export async function uploadBoardImage(
  dealId: string,
  file: File,
): Promise<UploadedBoardImage> {
  let uploadBlob: Blob = file;
  let extension = 'webp';

  if (isSupportedImageType(file)) {
    uploadBlob = await compressImageToWebP(file, { maxWidth: 2400, maxHeight: 2400 });
  } else {
    const dot = file.name.lastIndexOf('.');
    extension = dot >= 0 ? file.name.slice(dot + 1) : 'bin';
  }

  const dims = await readImageDimensions(uploadBlob);

  const safeBase = sanitizeFileNameForStoragePath(
    file.name.replace(/\.[^.]+$/, '') || 'image',
  );
  const storagePath = `${STORAGE_PREFIX}/${dealId}/${Date.now()}-${safeBase}.${extension}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, uploadBlob, {
    contentType: uploadBlob.type || file.type || 'application/octet-stream',
  });
  const url = await getDownloadURL(storageRef);

  return {
    url,
    storagePath,
    naturalWidth: dims.width || undefined,
    naturalHeight: dims.height || undefined,
  };
}

/** Delete a board image's underlying storage object (best-effort). */
export async function deleteBoardImage(storagePath?: string): Promise<void> {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch {
    // Object may already be gone — ignore.
  }
}

/** Normalize a user-typed URL into something fetchable. */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** Loose check for whether a pasted string looks like a URL. */
export function looksLikeUrl(text: string): boolean {
  const t = text.trim();
  if (!t || /\s/.test(t)) return false;
  return /^https?:\/\/\S+$/i.test(t) || /^[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(t);
}

export function domainFromUrl(url: string): string {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Fetch a link preview via the Cloud Function. On any failure, returns a
 * graceful fallback containing just the domain + a Google favicon so the link
 * card still renders something useful.
 */
export async function fetchLinkPreview(rawUrl: string): Promise<LinkPreviewResult> {
  const url = normalizeUrl(rawUrl);
  const domain = domainFromUrl(url);
  const fallback: LinkPreviewResult = {
    url,
    domain,
    title: domain,
    favicon: `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`,
  };

  try {
    const resp = await fetch(`${LINK_PREVIEW_ENDPOINT}?url=${encodeURIComponent(url)}`);
    if (!resp.ok) return fallback;
    const data = await resp.json();
    if (!data || data.success === false) return fallback;
    return {
      url,
      domain,
      title: data.title || fallback.title,
      description: data.description || undefined,
      image: data.image || undefined,
      favicon: data.favicon || fallback.favicon,
    };
  } catch (err) {
    console.warn('[leadBoardService] link preview failed, using fallback:', err);
    return fallback;
  }
}
